import React from "react";
import { Link } from "react-router-dom";
import LegalFooterBannerAndLinks from "components/legal/LegalFooterBannerAndLinks";

const STATS = [
  { label: "Algo-Trading Experience", value: "5+ Years" },
  { label: "Satisfaction Guarantee", value: "30 Days" },
  { label: "Execution Model", value: "Auto Copy" },
];

const HOW_IT_WORKS = [
  {
    step: "01",
    title: "Connect your trading account",
    description:
      "We help you connect your account safely so you can follow our strategy without manual order entry.",
  },
  {
    step: "02",
    title: "Our system executes automatically",
    description:
      "No Telegram signal chasing. Orders are copied automatically to reduce timing mistakes and execution delay.",
  },
  {
    step: "03",
    title: "Track performance with clear risk",
    description:
      "You keep control of lot size and risk settings while we continue to refine and upgrade our strategy engine.",
  },
];

const BENEFITS = [
  "Built by traders with 5+ years in algo-trading.",
  "Semi-automated strategy with a fully automated copy-execution experience.",
  "Historical stress tests reached 25% max drawdown under aggressive risk.",
  "With conservative sizing (e.g. 0.01 lot), drawdown can be significantly lower.",
  "30-day satisfaction guarantee.",
  "Continuous strategy R&D so clients receive high-quality setups only.",
];

/**
 * Public landing at `/` while the product is not fully launched.
 * E-mail capture → POST /api/waitlist (Supabase table waitlist_emails).
 */
export default function ComingSoon() {
  return (
    <div className="relative overflow-x-hidden bg-[#0B1437] pb-12 text-white sm:pb-16">
      <div
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(67, 97, 238, 0.35), transparent)",
        }}
      />

      <div className="relative z-10 mx-auto w-full max-w-6xl space-y-14 px-5">
        <header className="space-y-5">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
            <div className="mx-auto mt-2 max-w-[560px] text-center lg:mx-0 lg:mt-0">
              <Link to="/" aria-label="Home" className="block">
                <img
                  src={`${process.env.PUBLIC_URL}/AlphaSniperTrading-Black-RBG.png?v=20260501h`}
                  alt="AlphaSniper Trading"
                  className="h-auto w-full object-contain brightness-110 contrast-110 drop-shadow-[0_8px_20px_rgba(0,0,0,0.35)]"
                  decoding="async"
                />
              </Link>
              <img
                src={`${process.env.PUBLIC_URL}/powered-by-fxpro.svg`}
                alt="Powered by FxPro — Trade Like a Pro"
                className="mx-auto -mt-8 h-40 w-auto max-w-full object-contain sm:-mt-10 sm:h-48 lg:-mt-12 lg:h-56"
                width={220}
                height={168}
                decoding="async"
              />
              <h1 className="mt-5 text-center text-4xl font-extrabold leading-tight sm:text-5xl">
                Semi-automated algo trading, fully automated execution.
              </h1>
            </div>

            <div className="w-full max-w-lg lg:mx-0">
              <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4 shadow-xl backdrop-blur-sm">
                <Link to="/" aria-label="Home" className="mx-auto block max-w-[380px]">
                  <img
                    src={`${process.env.PUBLIC_URL}/AlphaSniperTradingLogo_sans_ecriture_sharpened-removebg-preview.png?v=20260501c`}
                    alt="AlphaSniper Trading logo"
                    className="h-auto w-full object-contain drop-shadow-[0_12px_28px_rgba(0,0,0,0.35)]"
                    decoding="async"
                  />
                </Link>
                <p className="mt-4 text-center text-base font-semibold leading-relaxed text-gray-200">
                  We do the market work. You receive the execution.
                </p>
              </div>

            <div className="mx-auto mt-3 w-full rounded-2xl border border-white/10 bg-white/[0.04] p-3 shadow-[0_10px_24px_rgba(0,0,0,0.35)] sm:p-4">
              <div className="grid grid-cols-3 gap-2 sm:gap-3">
                {STATS.map((item) => (
                  <div
                    key={item.label}
                    className="rounded-xl border border-white/10 bg-white/[0.04] px-2 py-3 text-center sm:px-3 sm:py-4"
                  >
                    <p className="text-base font-bold text-white sm:text-xl">
                      {item.value}
                    </p>
                    <p className="mt-1 text-[10px] leading-snug text-gray-300 sm:text-xs">
                      {item.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mx-auto mt-3 w-full rounded-2xl border border-slate-200/25 bg-slate-900/60 px-3 py-3 shadow-[0_10px_24px_rgba(0,0,0,0.35)]">
              <div className="flex items-center justify-center gap-5 text-center">
                <div className="relative h-[72px] w-[72px] rounded-full bg-gradient-to-b from-slate-200 via-slate-300 to-slate-500 p-[2px]">
                  <div className="grid h-full w-full place-items-center rounded-full bg-[#111827] ring-1 ring-white/10">
                    <div className="absolute inset-[7px] rounded-full border border-slate-400/35" />
                    <svg
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      className="h-7 w-7 text-emerald-300"
                      fill="currentColor"
                    >
                      <path d="M12 2 4 5v6c0 5 3.4 9.7 8 11 4.6-1.3 8-6 8-11V5l-8-3Zm3.7 7.6-4.2 4.2a1 1 0 0 1-1.4 0L8.3 12a1 1 0 1 1 1.4-1.4l1.1 1.1 3.5-3.5a1 1 0 1 1 1.4 1.4Z" />
                    </svg>
                  </div>
                </div>
                <div className="leading-tight">
                  <p className="text-sm font-semibold uppercase tracking-[0.24em] text-slate-200">
                    Certified
                  </p>
                  <p className="mt-1 text-2xl font-extrabold uppercase tracking-[0.06em] text-white">
                    30-Day Money Back
                  </p>
                  <p className="text-base font-semibold text-slate-200">
                    1st month guarantee
                  </p>
                </div>
              </div>
            </div>
            </div>
          </div>

          <div className="w-full text-left">
            <p className="text-base leading-relaxed text-gray-300 sm:text-lg">
              We built AlphaSniper for traders who want serious execution
              without living in Telegram channels all day. Our copy model is
              designed to reduce delay, improve consistency, and keep risk
              transparent. Want pricing and onboarding details?{" "}
              <Link
                to="/HowItWorks"
                className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
              >
                See how it works
              </Link>
              .
            </p>
          </div>
        </header>

        <section className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
          <div className="mb-6 flex flex-col gap-2 text-center sm:text-left">
            <h2 className="text-2xl font-bold text-white">Why traders choose us</h2>
            <p className="text-sm text-gray-300">
              Inspired by modern signal platforms, adapted for AlphaSniper with
              an execution-first approach.
            </p>
          </div>
          <ul className="grid gap-3 sm:grid-cols-2">
            {BENEFITS.map((item) => (
              <li
                key={item}
                className="rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-gray-200"
              >
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="space-y-6">
          <h2 className="text-center text-3xl font-bold">How it works</h2>
          <div className="grid gap-4 md:grid-cols-3">
            {HOW_IT_WORKS.map((item) => (
              <article
                key={item.step}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-5"
              >
                <p className="text-sm font-semibold text-brand-300">{item.step}</p>
                <h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-gray-300">
                  {item.description}
                </p>
              </article>
            ))}
          </div>
        </section>

        <section
          className="grid items-center gap-8 rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8 lg:grid-cols-2"
          id="waitlist"
        >
          <div className="space-y-4 text-center">
            <h2 className="text-3xl font-bold">
              Hurry, sign up and earn a 30-day money-back guarantee
            </h2>
            <p className="text-sm leading-relaxed text-gray-300 sm:text-base">
              Join now to secure your access and benefit from our first-month
              refund protection terms.
            </p>
            <p className="text-xs leading-relaxed text-gray-400">
              To be eligible for a refund under the first-month guarantee, you
              must use an AlphaSniper-designated{" "}
              <strong className="font-semibold text-gray-300">
                broker partner
              </strong>{" "}
              account as described in our{" "}
              <Link
                to="/RefundPolicy"
                className="font-medium text-brand-300 underline-offset-2 hover:text-brand-200 hover:underline"
              >
                Refund Policy
              </Link>{" "}
              (including verification links for partner onboarding).
            </p>
            <p className="text-xs text-gray-400">
              Risk disclaimer: trading involves risk and past performance does
              not guarantee future results.
            </p>
          </div>

          <div className="flex items-center justify-center rounded-2xl border border-white/10 bg-[#0a102c]/60 p-6 text-center shadow-xl backdrop-blur-sm">
            <Link
              to="/SignUp"
              className="inline-flex items-center justify-center rounded-xl bg-brand-500 px-12 py-4 text-lg font-semibold text-white transition hover:bg-brand-600 active:bg-brand-700"
            >
              Sign up
            </Link>
          </div>
        </section>

        <footer className="space-y-2 border-t border-white/10 pt-8 text-sm text-gray-400">
          <p className="text-center leading-relaxed">
            AlphaSniper 2026 © All Rights Reserved
          </p>
          <LegalFooterBannerAndLinks />

          <div className="mt-6 space-y-4 rounded-2xl border border-white/10 bg-[#0a102c]/60 p-4 text-left text-xs leading-relaxed text-gray-400 sm:p-5">
            <p>
              <span className="font-semibold text-gray-200">Regulatory Notice:</span>{" "}
              The information on this site is not directed at residents in any
              country or jurisdiction where such distribution or use would be
              contrary to local law or regulation.
            </p>

            <p>
              <span className="font-semibold text-gray-200">High Risk Warning:</span>{" "}
              Foreign exchange trading carries a high level of risk and may not
              be suitable for all investors. Leverage creates additional risk
              and loss exposure. Before you decide to trade foreign exchange,
              carefully consider your investment objectives, experience level,
              and risk tolerance. You could lose some or all of your initial
              investment; do not invest money that you cannot afford to lose.
              Educate yourself on the risks associated with forex trading, and
              seek advice from an independent financial or tax advisor if you
              have any questions. Any data and information are provided
              &quot;as is&quot; solely for informational purposes and are not intended for
              trading purposes or investment advice.
            </p>

            <p className="uppercase tracking-wide">
              Past performance results are not necessarily indicative of future
              results. No representation is being made that any account will or
              is likely to achieve profits or losses similar to those shown.
              Numerous factors related to market conditions and strategy
              implementation cannot be fully accounted for in historical
              performance results. Prospective clients should be particularly
              cautious about placing undue reliance on past performance and
              should not base their decision solely on previously presented
              outcomes. In making any investment decision, prospective clients
              must rely on their own examination of the trading decision-maker
              and the applicable agreement terms, including all merits and
              risks involved.
            </p>
          </div>
        </footer>
      </div>
    </div>
  );
}
